// Generic CRUD-клиент для owner-scoped публичных каталогов (casing/mud/field/…).
// Единый дом для fetch-boilerplate: list/create/update/remove + ensureOk +
// InUseError. Бэкенд-зеркало — `ssm_public/src/owned_crud.rs` (тот же контракт:
// owner-scope, 404 на чужой/нет, 409 на FK RESTRICT). Конкретный клиент только
// параметризует DTO/Create/Update-типы (ts-rs @webtypes), базовый путь и текст
// сообщения про «используется».

export async function ensureOk(resp: Response): Promise<void> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${resp.status} ${resp.statusText}${text ? ': ' + text : ''}`)
  }
}

// Бросается при DELETE → 409: запись используется по FK ON DELETE RESTRICT.
// `message` задаёт конкретный клиент (своя формулировка про casing/mud/…), а
// `instanceof InUseError` на странице ловит этот случай отдельно от прочих ошибок.
export class InUseError extends Error {}

const JSON_HEADERS = { 'content-type': 'application/json' }

export type CrudClient<Dto, Create, Update> = {
  list:   () => Promise<Dto[]>
  create: (body: Create) => Promise<Dto>
  update: (id: number, body: Update) => Promise<Dto>
  remove: (id: number) => Promise<void>
}

export function crudClient<Dto, Create, Update>(
  base: string,
  inUseMessage: string,
): CrudClient<Dto, Create, Update> {
  return {
    async list(): Promise<Dto[]> {
      const resp = await fetch(base, { credentials: 'same-origin' })
      await ensureOk(resp)
      return resp.json()
    },
    async create(body: Create): Promise<Dto> {
      const resp = await fetch(base, {
        method:      'POST',
        credentials: 'same-origin',
        headers:     JSON_HEADERS,
        body:        JSON.stringify(body),
      })
      await ensureOk(resp)
      return resp.json()
    },
    async update(id: number, body: Update): Promise<Dto> {
      const resp = await fetch(`${base}/${id}`, {
        method:      'PATCH',
        credentials: 'same-origin',
        headers:     JSON_HEADERS,
        body:        JSON.stringify(body),
      })
      await ensureOk(resp)
      return resp.json()
    },
    async remove(id: number): Promise<void> {
      const resp = await fetch(`${base}/${id}`, {
        method:      'DELETE',
        credentials: 'same-origin',
      })
      if (resp.status === 409) throw new InUseError(inUseMessage)
      await ensureOk(resp)
    },
  }
}
